<script setup lang="ts">
import { storeToRefs } from "pinia";
import { computed, ref, watch } from "vue";

import { useRoute } from "vue-router";

import { useToast } from "vue-toastification";

import LoadingSpinner from "@/components/Reusables/Loading/LoadingSpinner.vue";

import { useModalStore } from "@/stores/modals/modals.store";

import { GetSpecialConditionTypeMappingByTypeIdentifier } from "@/api/services/Mapping";
import { useQuery, useMutation } from "@vue/apollo-composable";

import GetProjectSpecialConditionTypeMappings from "@/graphql/queries/GetProjectSpecialConditionTypeMappings";
import InsertProjectSpecialConditionTypeMapping from "@/graphql/mutations/InsertProjectSpecialConditionTypeMapping";
import DeleteProjectSpecialConditionTypeMapping from "@/graphql/mutations/DeleteProjectSpecialConditionTypeMapping";

import TextInputNew from "@/components/Reusables/Inputs/TextInputNew.vue";
import SelectInputNew from "@/components/Reusables/Inputs/SelectInputNew.vue";
import InputLabel from "@/components/Reusables/Inputs/InputLabel.vue";

import InfoIcon from "@/features/Tooltip/InfoIcon.vue";
import InfoModalButton from "@/features/Tooltip/InfoModalButton.vue";

import { useProject } from "@/stores/project/project.store";

import { useProjectRevisionsStore } from "@/pages/ProjectVersionHistory/stores/projectRevisions.store";
import { useProjectEditReadOnly } from "@/pages/ProjectVersionHistory/composables/useProjectEditReadOnly.composable";
import RevisionGatedButton from "@/pages/ProjectVersionHistory/components/revisions/RevisionGatedButton.vue";
import { revisionScopeFilter } from "@/pages/ProjectVersionHistory/services/revisionScope";
import {
  isVersionEntityTypeUnsupported,
  isVersioningSchemaMissing,
} from "@/pages/ProjectVersionHistory/services/versioningSchema";
import { buildSpecialConditionVersionInput } from "@/pages/ProjectVersionHistory/utils/specialConditionVersionSnapshot";
import InsertProjectSpecialConditionTypeMappingWithVersionMutation from "@/graphql/mutations/InsertProjectSpecialConditionTypeMappingWithVersionMutation";
import DeleteProjectSpecialConditionTypeMappingWithVersionMutation from "@/graphql/mutations/DeleteProjectSpecialConditionTypeMappingWithVersionMutation";
import gaiupApolloClient from "@/graphql/services/ApolloClient";

import { useDashboard } from "@/stores/dashboard/dashboard.store";

import * as Sentry from "@sentry/vue";

import { useGetSpecialConditionTypesQuery } from "@/graphql";

const route = useRoute();

const modals = useModalStore();

const projectStore = useProject();
const { project, selectedModel } = storeToRefs(projectStore);

// Reflect a special-condition change on the dashboard/BCI without a page reload — the mapping feeds
// the project's emission total.
const { refreshDashboard } = useDashboard();

const props = defineProps<{
  identifier: any;
  hasSpecialConditionTypeMapping: boolean;
  modelName?: string;
}>();

const model = computed(() => props.modelName ?? selectedModel.value);

const specialConditionTypes = ref<any>();

const selectedRoleId = ref<string>("");

const description = ref("");

const projectUnit = ref("m²");
const projectValue = ref<number | null>(null);

const constructionUnit = ref("m²");
const constructionValue = ref<number | null>(null);

const lengthUnit = ref("m");
const lengthValue = ref<number | null>(null);

const referenceUnit = ref("kgCO₂e/m²");
const referenceValue = ref<number | null>(null);

const totalMaterialUnit = ref("kgCO₂e");
const totalMaterialValue = ref<number | null>(null);

const specialConditionUnit = ref("kgCO₂e");
const specialConditionValue = ref<number | null>(null);

const label = (base: string, unit?: string) => (unit ? `${base} (${unit})` : base);

const totalAreaLabel = computed(() => label("Total area", projectUnit.value));
const constructionLabel = computed(() => label("Area of construction", constructionUnit.value));
const lengthLabel = computed(() => label("Length", lengthUnit.value));
const referenceLabel = computed(() => label("Reference value", referenceUnit.value));
const totalMaterialLabel = computed(() => label("Impact from materials", totalMaterialUnit.value));
const specialConditionLabel = computed(() => label("Impact from special conditions", specialConditionUnit.value));

const calculationTypeId = computed(
  () => project.value?.emissionCalculationAssumptions?.calculationType?.calculationTypeEnum ?? null
);

const { onResult: onResultSpecial } = useGetSpecialConditionTypesQuery(
  () => ({ calculationTypeId: calculationTypeId.value! }),
  () => ({ enabled: calculationTypeId.value !== null })
);

onResultSpecial(({ data }) => {
  if (!data?.specialConditionType) return;

  specialConditionTypes.value = data.specialConditionType;
});

const parameter = computed(() => {
  // Two independent queries: this row's own mapping (which sets selectedRoleId) and the full type
  // list can resolve in either order, so a fresh mount on an already-mapped row can see an id before
  // the list that would resolve it exists yet.
  if (!selectedRoleId.value || !specialConditionTypes.value) return null;
  const role = specialConditionTypes.value.find((r: { id: any }) => String(r.id) === selectedRoleId.value);
  return role?.specialConditionTypeCategory?.specialConditionTypeInputParameter?.parameter?.[0] ?? null;
});

const applyMapping = async (payload: any) => {
  description.value = payload?.description ?? "";

  projectUnit.value = payload?.dimensions?.project?.unit ?? projectUnit.value;
  projectValue.value = payload?.dimensions?.project?.value ?? null;

  constructionUnit.value = payload?.dimensions?.areaOfConstruction?.unit ?? constructionUnit.value;
  constructionValue.value = payload?.dimensions?.areaOfConstruction?.value ?? null;

  lengthUnit.value = payload?.dimensions?.length?.unit ?? lengthUnit.value;
  lengthValue.value = payload?.dimensions?.length?.value ?? null;

  referenceUnit.value = payload?.emissions?.referenceValue?.unit ?? referenceUnit.value;
  referenceValue.value = payload?.emissions?.referenceValue?.value ?? null;

  totalMaterialUnit.value = payload?.emissions?.totalMaterialEmission?.unit ?? totalMaterialUnit.value;
  totalMaterialValue.value = payload?.emissions?.totalMaterialEmission?.value ?? null;

  specialConditionUnit.value = payload?.emissions?.specialConditionIncreasedEmission?.unit ?? specialConditionUnit.value;
  specialConditionValue.value = payload?.emissions?.specialConditionIncreasedEmission?.value ?? null;
};

const projectId = computed(() => String(route.params.id ?? ""));

const enabled = computed(() => Boolean(projectId.value));

const isLoading = ref(true);

// A revision's special-condition mappings are its OWN rows — the table carries revision_id since
// RTLCA-5259 — so previewing a frozen revision is this same query with a different filter. The
// filter rides in the reactive variables, so changing the pin refetches by itself.
const revisionsStore = useProjectRevisionsStore();
const revisionFilter = computed(() => revisionScopeFilter(revisionsStore.readPinRevisionIdFor(projectId.value)));

// Writes always land on the working state (the BEFORE INSERT trigger stamps the current revision),
// so saving while previewing would quietly edit today's project while showing yesterday's; a
// Viewer's write is refused outright by Hasura's row permissions. `useProjectEditReadOnly` answers
// for both and words each refusal for its own reason (RTLCA-5253, RTLCA-5582).
const { isReadOnly, blockedByPreview } = useProjectEditReadOnly("top");

const { onResult } = useQuery(
  GetProjectSpecialConditionTypeMappings,
  () => ({
    buildingProjectId: projectId.value,
    model: model.value,
    typeIdentifiers: Array.isArray(props.identifier) ? props.identifier : [props.identifier],
    revisionFilter: revisionFilter.value,
  }),
  {
    enabled,
    fetchPolicy: "network-only",
    notifyOnNetworkStatusChange: false,
  }
);

onResult(async ({ data }) => {
  if (!data?.projectSpecialConditionTypeMapping || data?.projectSpecialConditionTypeMapping.length === 0) {
    isLoading.value = false;
    return;
  }

  const id = data.projectSpecialConditionTypeMapping[0].specialConditionType.id;
  selectedRoleId.value = id;
  isLoading.value = false;
});

const { mutate: saveMapping } = useMutation(InsertProjectSpecialConditionTypeMapping);

const { mutate: deleteMapping } = useMutation(DeleteProjectSpecialConditionTypeMapping);

// Neither composable's own `loading` tracked the real wait: handleSave/handleDelete call
// gaiupApolloClient.mutate directly for the bundled document and only reach `saveMapping`/
// `deleteMapping` on the fallback path, so on any project with the trail schema — the normal case —
// `loading` stayed false for the whole request and the button showed no busy state at all.
const isSaving = ref(false);
const isDeleting = ref(false);

// Describes the button as if writing were allowed — RevisionGatedButton overrides it while blocked
// so the tooltip explaining the block can still fire.
const disableDelete = computed(() => {
  if (props.hasSpecialConditionTypeMapping === false || (props.hasSpecialConditionTypeMapping && isDeleting.value)) {
    return true;
  }

  return false;
});

// Every app-written change to project data leaves a trail row, bundled in the same document so the
// change and its history line share one transaction. Special conditions were the last mapping
// change writing nothing — see specialConditionVersionSnapshot.ts for why (the entity_type check
// constraint had no value for them until Hasura migration 1787000000000).
const typeLabel = computed(() => {
  const selected = specialConditionTypes.value?.find((type: { id: unknown }) => String(type.id) === selectedRoleId.value);
  return selected?.buildingPart?.label ?? null;
});

/** Runs the bundled document, falling back to the plain one where the trail cannot be written yet. */
const withVersionRow = async (bundled: () => Promise<unknown>, plain: () => Promise<unknown>) => {
  try {
    await bundled();
  } catch (mutationError) {
    // The user's save must not depend on the trail existing (rollout: Hasura → API → UI).
    if (!isVersioningSchemaMissing(mutationError) && !isVersionEntityTypeUnsupported(mutationError)) throw mutationError;
    Sentry.captureMessage("Special-condition change saved without a trail row — versioning schema is behind here");
    await plain();
  }
};

const handleDelete = async () => {
  if (blockedByPreview()) return;

  const currentProjectId = project.value!.id;
  const materialModel = model.value!;
  const typeIdentifier = props.identifier;

  const version = buildSpecialConditionVersionInput({
    projectId: currentProjectId,
    operation: "delete",
    summary: "Special condition removed",
    entityLabel: typeLabel.value ?? String(typeIdentifier),
  });

  isDeleting.value = true;
  try {
    await withVersionRow(
      () =>
        gaiupApolloClient.mutate({
          mutation: DeleteProjectSpecialConditionTypeMappingWithVersionMutation,
          variables: { projectId: currentProjectId, model: materialModel, typeIdentifier, version },
        }),
      () => deleteMapping({ projectId: currentProjectId, model: materialModel, typeIdentifier })
    );

    // Closed on success rather than after the refresh: the write is already done, and the
    // dashboard's own figure was stale until this refresh regardless of who is waiting for it.
    useToast().success("Special condition deleted");
    modals.close({ action: "deleted", id: props.identifier });
    void refreshDashboard().catch((error: unknown) => Sentry.captureException(error));
  } catch (error) {
    Sentry.captureException(error);
    useToast().error("The special condition could not be deleted. Try again.");
  } finally {
    isDeleting.value = false;
  }
};

const handleSave = async () => {
  if (blockedByPreview()) return;

  const typeId = selectedRoleId.value;
  const typeIdentifier = props.identifier;

  const mappingInput = {
    projectId: project.value!.id,
    model: model.value!,
    typeIdentifier,
    specialConditionTypeId: typeId,
    areaOfConstruction: constructionValue.value ?? null,
    description: description.value ?? null,
    length: lengthValue.value ?? null,
  };

  // The upsert cannot tell create from update, so the trail says "saved" — the operation still has
  // to be one of the three the check constraint allows, and update is the honest default for a row
  // that may well have existed.
  const version = buildSpecialConditionVersionInput({
    projectId: mappingInput.projectId,
    operation: "update",
    summary: "Special condition saved",
    entityLabel: typeLabel.value ?? String(typeIdentifier),
  });

  isSaving.value = true;
  try {
    await withVersionRow(
      () =>
        gaiupApolloClient.mutate({
          mutation: InsertProjectSpecialConditionTypeMappingWithVersionMutation,
          variables: { ...mappingInput, version },
        }),
      () => saveMapping(mappingInput)
    );

    useToast().success("Special condition saved");
    modals.close({ action: "saved", id: props.identifier });
    void refreshDashboard().catch((error: unknown) => Sentry.captureException(error));
  } catch (error) {
    Sentry.captureException(error);
    useToast().error("The special condition could not be saved. Try again.");
  } finally {
    isSaving.value = false;
  }
};

// The API computes the type's impact from the row's included, EPD-mapped materials and answers 500
// "Type identifier '…' not found on model '…'" when there is none. The rows keep their control off in
// that state, but a material can lose its mapping while this modal is open, so the message is still
// worth having over "Something went wrong".
const describeLoadMappingFailure = (error: unknown): string => {
  const detail = (error as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
  if (typeof detail === "string" && detail.includes("not found on model")) {
    return "No mapped material for this type on the model yet. Map it to an EPD first. Special conditions build on its impact.";
  }
  return "The impact for this type could not be loaded. Try again.";
};

// The description and the dimensions come from this REST read, NOT from the Hasura query above —
// that one only tells us which type is selected. So it needs the same pin, or a preview renders a
// frozen revision's type with the working state's description (RTLCA-5259). Resolved at call time,
// never captured, per the as-of read rule.
const loadMapping = async (typeId: string) => {
  if (!typeId) return;

  try {
    isLoading.value = true;

    const res = await GetSpecialConditionTypeMappingByTypeIdentifier({
      buildingProjectId: project.value!.id,
      dimensions: {
        areaOfConstruction: constructionValue.value ?? undefined,
        length: lengthValue.value ?? undefined,
      },
      model: model.value!,
      typeIdentifier: props.identifier,
      specialConditionTypeId: typeId,
      revisionId: revisionsStore.readPinRevisionIdFor(projectId.value) ?? undefined,
    });

    await applyMapping(res);
  } catch (e) {
    console.error("Load mapping for selectedRoleId failed:", e);
    useToast().error(describeLoadMappingFailure(e));
  } finally {
    isLoading.value = false;
  }
};

// Two-part key, the one ProjectDetailLayout established: the pin covers previewing, the active
// revision id covers an admin switching the project underneath us. Watching selectedRoleId alone
// was not enough — revisions usually agree on the TYPE, so the id would not change and the stale
// description would survive the pin change that was supposed to replace it.
watch(
  [
    selectedRoleId,
    () => revisionsStore.readPinRevisionIdFor(projectId.value),
    () => revisionsStore.activeMetaFor(projectId.value)?.id,
  ],
  ([typeId]) => loadMapping(typeId)
);
</script>

<template>
  <div class="pb-2">
    <div class="flex items-center pb-1">
      <h4>Special conditions</h4>
      <InfoModalButton markdown-path="specialConditions" />
    </div>
    <p class="rounded-4 w-fit text-sm font-bold">
      {{ identifier }}
    </p>
  </div>

  <div class="mt-1 flex flex-col gap-4">
    <div class="flex flex-col gap-4">
      <!-- The three genuinely editable controls here (type, quantity, description) obey the same
           guard as Save; the rest are read-only computed displays and were already `disabled`. -->
      <SelectInputNew
        v-model="selectedRoleId"
        tooltip="Select the construction type to which the special condition applies. This determines which reference value and formular is used in the calculation."
        label="Type"
        :disabled="isReadOnly">
        <option
          disabled
          value="">
          Select type
        </option>
        <option
          v-for="condition in specialConditionTypes"
          :key="condition.specialConditionTypeCategory.id"
          :value="condition.id">
          {{ condition.buildingPart.label }}
        </option>
      </SelectInputNew>
      <section class="relative">
        <div
          v-if="isLoading"
          class="">
          <LoadingSpinner
            class="absolute-center"
            size="lg"
            fill="primary" />
        </div>

        <div
          class="flex flex-col gap-4"
          :class="{ 'opacity-0': isLoading }">
          <div class="flex items-end gap-4">
            <TextInputNew
              v-model="totalMaterialValue"
              :label="totalMaterialLabel"
              color="primary"
              tooltip="The total climate impact of the selected construction, including all materials. This is used to check whether the special condition results in a higher impact than normal."
              disabled />

            <TextInputNew
              v-if="parameter === 'areaOfConstruction'"
              v-model="constructionValue"
              :label="constructionLabel"
              color="primary"
              type="number"
              :disabled="isReadOnly"
              tooltip="Enter the quantity of the construction in the correct unit. This is multiplied by the reference value to determine the standard impact for the chosen construction type." />
            <TextInputNew
              v-else-if="parameter === 'length'"
              v-model="lengthValue"
              :label="lengthLabel"
              type="number"
              color="primary"
              :disabled="isReadOnly" />
          </div>

          <div class="flex items-end gap-4">
            <TextInputNew
              v-model="projectValue"
              :label="totalAreaLabel"
              color="primary"
              disabled
              tooltip="The building's reference area. This is used in the formula to calculate the difference between the actual impact and the reference impact." />
            <TextInputNew
              v-model="referenceValue"
              :label="referenceLabel"
              color="primary"
              disabled
              tooltip="
                       The standard climate impact for an average construction of the selected type. If the actual impact is higher due to special conditions, this value is used to calculate the baseline that the building is compared against.Note: when the reference value is based on area, it refers to the area of the construction, not the building area." />
          </div>

          <TextInputNew
            v-model="specialConditionValue"
            :label="specialConditionLabel"
            color="primary"
            disabled
            tooltip="
                       The calculated additional impact caused by the special condition.

                       <ul class='max-w-md pt-2 space-y-1 list-disc list-outside pl-5 text-body'>
                        <li>
                            If the value is 0, the construction does not exceed the standard reference and no deduction is applied.
                        </li>
                        <li>
                           If the value is greater than 0, this amount is subtracted from the building's total climate impact.
                        </li>
                    </ul>" />

          <div>
            <div class="flex items-center pb-1">
              <InputLabel
                id="description"
                label="Description" />
              <InfoIcon tooltip="Use this field to briefly document why the special condition applies to this construction." />
            </div>
            <textarea
              id="description"
              v-model="description"
              :disabled="isReadOnly"
              class="rounded-8 focus-input w-full resize-none border border-slate-500 p-3 text-sm leading-none disabled:cursor-not-allowed disabled:bg-slate-200" />
          </div>
        </div>
      </section>
    </div>
    <div class="flex justify-between">
      <RevisionGatedButton
        label="Delete"
        color="destructive"
        class="w-fit"
        :disabled="disableDelete"
        :is-loading="isDeleting"
        :guard="useProjectEditReadOnly"
        @click="handleDelete" />
      <RevisionGatedButton
        label="Save"
        color="primary"
        class="w-fit"
        :is-loading="isSaving"
        :guard="useProjectEditReadOnly"
        @click="handleSave" />
    </div>
  </div>
</template>

<style lang="scss" scoped>
.v-enter-active,
.v-leave-active {
  transition: opacity 0.5s ease;
}

.v-enter-from,
.v-leave-to {
  opacity: 0;
}
</style>
